import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, Users, Target, Compass, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHeader } from "./ServicesPage";
import { MEDIA } from "@/constants/business";

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Vila Elevators & Escalators | Bangalore's Trusted Lift Manufacturer"
        description="Vila Elevators is a Bangalore-based elevator and escalator manufacturer serving Karnataka, Andhra Pradesh, Telangana and Tamil Nadu with in-house CNC production."
        path="/about"
        keywords="about vila elevators, elevator company bangalore, best lift manufacturer karnataka"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          mainEntity: {
            "@type": "LocalBusiness",
            "@id": "https://vilaelevators.in/#business",
            name: "Vila Elevators & Escalators",
            description:
              "Vila Elevators & Escalators is an elevator and escalator manufacturing company headquartered in Bommasandra Industrial Area, Bengaluru, Karnataka, India. The company manufactures passenger elevators, home lifts, freight elevators, hospital lifts, escalators and moving walkways in-house using CNC precision machinery, and provides installation, AMC maintenance and modernization services across Karnataka, Tamil Nadu, Telangana and Andhra Pradesh.",
            url: "https://vilaelevators.in",
            telephone: "+919071221100",
            email: "info@vilaelevators.in",
          },
        }}
      />

      <PageHeader
        eyebrow="About Us"
        title="Engineering vertical journeys, one building at a time."
        desc="Vila Elevators & Escalators is a Bengaluru-headquartered manufacturer with a mission to raise the standard of vertical transportation across South India."
        image={MEDIA.lobby}
      />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Our Story</div>
            <h2 className="font-display text-4xl font-bold text-[#1a1a1a] mb-6">
              Founded on trust. Built on precision.
            </h2>
            <div className="space-y-5 text-[#6b6b6b] leading-relaxed">
              <p className="text-[#4a4a4a] font-medium">
                Vila Elevators & Escalators is an elevator and escalator manufacturing company based in Bengaluru (Bangalore), Karnataka, India — not to be confused with generic "villa elevators". We design, manufacture, install and maintain passenger elevators, home lifts, freight elevators, hospital lifts, escalators and moving walkways from our own CNC production facility at Bommasandra Industrial Area, serving Karnataka, Tamil Nadu, Telangana and Andhra Pradesh.
              </p>
              <p>
                Vila Elevators & Escalators was founded to solve a simple problem — India needed a reliable, home-grown manufacturer that combined global engineering standards with local service responsiveness.
              </p>
              <p>
                From our facility in Bommasandra Industrial Area, we now serve residential builders, hospitals, hotels, malls, factories and metro projects across four states — with the same obsessive attention to safety and finish that we started with.
              </p>
              <p>
                Every elevator we ship carries the fingerprints of engineers, machinists, and installers who take pride in what they build. That's the difference.
              </p>
            </div>
          </motion.div>
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            src={MEDIA.productElevator}
            alt="Vila passenger elevator cabin"
            className="w-full aspect-[4/5] object-cover border border-[#e5ddd0]"
          />
        </div>
      </section>

      <section className="py-20 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <Value icon={<Target />} title="Safety First" desc="Every design prioritizes passenger safety, always." />
            <Value icon={<Award />} title="Precision Craft" desc="Sub-mm tolerances via CNC manufacturing." />
            <Value icon={<Users />} title="Customer Obsessed" desc="Response teams available 24×7." />
            <Value icon={<Compass />} title="Local + Global" desc="Global engineering, local service." />
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-6">Let's build together.</h2>
          <p className="text-[#6b6b6b] mb-8">Whether you're a builder, architect, or facility manager — talk to us.</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors"
          >
            Get In Touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function Value({ icon, title, desc }) {
  return (
    <div>
      <div className="w-12 h-12 border border-[#b08d57]/40 text-[#b08d57] flex items-center justify-center mb-4">{icon}</div>
      <h4 className="font-display text-lg font-bold text-[#1a1a1a] mb-2">{title}</h4>
      <p className="text-[#6b6b6b] text-sm">{desc}</p>
    </div>
  );
}
